// src/pages/LessonReaderPage.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  LinearProgress,
  Typography,
  Stack,
  Button,
  Paper,
  Divider,
  Chip,
} from '@mui/material';
import AssignmentIcon from '@mui/icons-material/Assignment';
import QuizIcon from '@mui/icons-material/Quiz';
import { supabase, markingDb } from '../supabaseClient.ts';
import RichText from '../components/RichText.tsx';
import QuestionRunner from '../components/QuestionRunner.tsx';
import { useTutorSession } from '../context/TutorSessionContext.tsx';
import { logTutorSessionEvent } from '../utils/tutorSessionEvents.ts';
import { useEffectiveStudentId } from '../hooks/useEffectiveStudentId.ts';

type Lesson = {
  id: string;
  title: string | null;
  category_id: string | null;
  subject_id: string | null;
  company_id: string | null;
  content: string;
  updated_at: string | null;
  metadata: Record<string, any> | null;
};

type LinkedTest = {
  id: string;
  name: string;
  description: string | null;
};

type TestAttempt = {
  id: string;
  score: number;
  total_marks: number;
};

type Segment =
  | { kind: 'html'; html: string }
  | { kind: 'question'; id: string };

const splitIntoSegments = (html: string): Segment[] => {
  // Parse HTML into a detached container
  const container = document.createElement('div');
  container.innerHTML = html || '<p></p>';

  const segments: Segment[] = [];
  let buffer = '';

  const flushBuffer = () => {
    if (buffer.trim().length) segments.push({ kind: 'html', html: buffer });
    buffer = '';
  };

  // Only iterate top-level children
  Array.from(container.childNodes).forEach((node) => {
    if (node.nodeType === Node.ELEMENT_NODE && (node as Element).tagName.toLowerCase() === 'question') {
      // Found a <question/> block
      const el = node as Element;
      const id = el.getAttribute('id') || '';
      flushBuffer();
      if (id) segments.push({ kind: 'question', id });
    } else {
      // Accumulate any non-question node's *outer* HTML
      if ((node as Element).outerHTML) buffer += (node as Element).outerHTML;
      else buffer += node.textContent || '';
    }
  });

  flushBuffer();
  return segments.length ? segments : [{ kind: 'html', html }];
};

const LessonReaderPage: React.FC = () => {
  const { tutorSession } = useTutorSession();
  const { studentId, loading: studentIdLoading } = useEffectiveStudentId();
  const { lessonId } = useParams<{ lessonId: string }>();
  const navigate = useNavigate();
  const [lesson, setLesson] = useState<Lesson | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [linkedTest, setLinkedTest] = useState<LinkedTest | null>(null);
  const [testAttempt, setTestAttempt] = useState<TestAttempt | null>(null);
  const [resolvedTagIds, setResolvedTagIds] = useState<string[]>([]);

  useEffect(() => {
    if (!lessonId || studentIdLoading) return;
    (async () => {
      setLoading(true);
      setErr(null);

      if (!studentId) {
        setErr('Student record not found.');
        setLesson(null);
        setLoading(false);
        return;
      }

      const { data: student, error: studentError } = await supabase
        .from('students')
        .select('company_id')
        .eq('id', studentId)
        .maybeSingle();
      if (studentError || !student) {
        setErr(studentError?.message ?? 'Student record not found.');
        setLesson(null);
        setLoading(false);
        return;
      }

      const { data: studentSubjects, error: studentSubjectsError } = await supabase
        .from('student_subjects')
        .select('subject_id')
        .eq('student_id', studentId);
      if (studentSubjectsError) {
        setErr(studentSubjectsError.message);
        setLesson(null);
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('lessons')
        .select('id, title, category_id, subject_id, company_id, content, updated_at, metadata')
        .eq('id', lessonId)
        .single();

      if (error || !data) {
        setErr(error?.message ?? 'Lesson not found.');
        setLesson(null);
        setLoading(false);
        return;
      }

      if (student.company_id && data.company_id && data.company_id !== student.company_id) {
        setErr('You do not have access to this lesson.');
        setLesson(null);
        setLoading(false);
        return;
      }

      const visibleSubjectIds = new Set(
        (studentSubjects ?? [])
          .map((r: any) => r.subject_id as string | null)
          .filter((id): id is string => Boolean(id)),
      );
      if (visibleSubjectIds.size > 0) {
        let lessonSubjectId: string | null = data.subject_id ?? null;

        if (!lessonSubjectId && data.category_id) {
          let cursorId: string | null = data.category_id;
          let hops = 0;
          while (cursorId && hops < 8) {
            const { data: catRow, error: catErr } = await supabase
              .from('categories')
              .select('id, parent_category_id, subject_id')
              .eq('id', cursorId)
              .maybeSingle();
            if (catErr || !catRow) break;
            if (catRow.subject_id) {
              lessonSubjectId = catRow.subject_id;
              break;
            }
            cursorId = catRow.parent_category_id ?? null;
            hops += 1;
          }
        }

        if (lessonSubjectId && !visibleSubjectIds.has(lessonSubjectId)) {
          setErr('You do not have access to this lesson.');
          setLesson(null);
          setLoading(false);
          return;
        }
      }

      setLesson(data as Lesson);
      if (tutorSession?.id && data?.id) {
        void logTutorSessionEvent(tutorSession.id, 'lesson_view', {
          lesson_id: data.id,
          title: data.title ?? '',
          category_id: data.category_id ?? null,
          viewed: true,
        });
      }
      setLoading(false);

      // Resolve tags from lesson metadata to tag IDs for practice link
      const meta = data.metadata as Record<string, any> | null;
      const directIds: string[] = [];
      const tagNames: string[] = [];
      if (meta) {
        for (const key of ['tags', 'tagNames', 'tag_names']) {
          if (!Array.isArray(meta[key])) continue;
          meta[key].forEach((item: unknown) => {
            if (typeof item === 'string' && item.trim()) {
              tagNames.push(item.trim());
            } else if (item && typeof item === 'object') {
              const obj = item as Record<string, unknown>;
              if (typeof obj.id === 'string') directIds.push(obj.id);
              else if (typeof obj.name === 'string' && obj.name.trim()) tagNames.push(obj.name.trim());
            }
          });
          break;
        }
      }
      if (directIds.length > 0) {
        setResolvedTagIds(directIds);
      } else if (tagNames.length > 0) {
        const { data: tagRows } = await supabase
          .from('tags')
          .select('id, name')
          .in('name', tagNames);
        if (tagRows) setResolvedTagIds(tagRows.map((r: any) => r.id as string));
      }

      // Fetch correlated test via lesson_tests junction
      const { data: ltRows } = await supabase
        .from('lesson_tests')
        .select('test_id, sort_order, tests!inner(id, name, description)')
        .eq('lesson_id', data.id)
        .order('sort_order')
        .limit(1);

      if (ltRows && ltRows.length > 0) {
        const testRow = (ltRows[0] as any).tests;
        const test: LinkedTest = {
          id: testRow.id,
          name: testRow.name ?? 'Test',
          description: testRow.description ?? null,
        };
        setLinkedTest(test);

        // Check if the student has attempted this test
        const { data: resultRows } = await markingDb
          .from('test_results')
          .select('id, score, total_marks')
          .eq('test_id', test.id)
          .eq('student_id', studentId)
          .order('finished_at', { ascending: false })
          .limit(1);

        if (resultRows && resultRows.length > 0) {
          setTestAttempt(resultRows[0] as TestAttempt);
        }
      }
    })();
  }, [lessonId, studentId, studentIdLoading, tutorSession?.id]);

  const segments = useMemo(
    () => (lesson?.content ? splitIntoSegments(lesson.content) : []),
    [lesson?.content]
  );

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', p: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h5">{lesson?.title || 'Lesson'}</Typography>
        <Button onClick={() => navigate(-1)}>Back</Button>
      </Stack>

      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {err && <Typography color="error" sx={{ mb: 2 }}>{err}</Typography>}

      {lesson && (
        <Box sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2 }}>
          {segments.map((seg, i) =>
            seg.kind === 'html' ? (
              <RichText key={`html-${i}`} html={seg.html} />
            ) : (
              <QuestionRunner key={`q-${seg.id}-${i}`} id={seg.id} />
            )
          )}

          {lesson.updated_at && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
              Last updated{' '}
              {new Date(lesson.updated_at).toLocaleString('en-AU', {
                dateStyle: 'medium',
                timeStyle: 'short',
                timeZone: 'Australia/Sydney',
              })}
            </Typography>
          )}
        </Box>
      )}

      {linkedTest && !loading && (
        <>
          <Divider sx={{ my: 3 }} />
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              display: 'flex',
              alignItems: 'center',
              gap: 2.5,
              borderRadius: 2,
            }}
          >
            <AssignmentIcon sx={{ fontSize: 40, color: 'primary.main', flexShrink: 0 }} />

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.8 }}>
                Lesson Test
              </Typography>
              <Typography variant="h6" sx={{ lineHeight: 1.3 }}>
                {linkedTest.name}
              </Typography>
              {linkedTest.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {linkedTest.description}
                </Typography>
              )}
              {testAttempt && (
                <Chip
                  size="small"
                  label={`Best: ${testAttempt.score} / ${testAttempt.total_marks}`}
                  color="success"
                  variant="outlined"
                  sx={{ mt: 1 }}
                />
              )}
            </Box>

            <Stack spacing={1} sx={{ flexShrink: 0 }}>
              <Button
                variant="contained"
                onClick={() => navigate(`/tests/${linkedTest.id}`)}
              >
                {testAttempt ? 'Retake Test' : 'Start Test'}
              </Button>
              {testAttempt && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() =>
                    navigate(`/tests/${linkedTest.id}/result/${testAttempt.id}`)
                  }
                >
                  View Results
                </Button>
              )}
            </Stack>
          </Paper>
        </>
      )}

      {lesson && !loading && (lesson.category_id || lesson.subject_id || resolvedTagIds.length > 0) && (() => {
        const params = new URLSearchParams();
        if (lesson.category_id) params.set('tree', `cat_${lesson.category_id}`);
        else if (lesson.subject_id) params.set('tree', `subject_${lesson.subject_id}`);
        if (resolvedTagIds.length > 0) params.set('tags', resolvedTagIds.join(','));
        return (
          <>
            {!linkedTest && <Divider sx={{ my: 3 }} />}
            <Paper
              variant="outlined"
              sx={{
                p: 3,
                display: 'flex',
                alignItems: 'center',
                gap: 2.5,
                borderRadius: 2,
                mt: linkedTest ? 2 : 0,
              }}
            >
              <QuizIcon sx={{ fontSize: 40, color: 'secondary.main', flexShrink: 0 }} />

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.8 }}>
                  Practice Questions
                </Typography>
                <Typography variant="h6" sx={{ lineHeight: 1.3 }}>
                  Practise what you've learned
                </Typography>
              </Box>

              <Button
                variant="contained"
                color="secondary"
                sx={{ flexShrink: 0 }}
                onClick={() => navigate(`/practice?${params.toString()}`)}
              >
                Start Practice
              </Button>
            </Paper>
          </>
        );
      })()}
    </Box>
  );
};

export default LessonReaderPage;
